import cx from 'clsx';
import React, { useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as ViewDetailsIcon } from '@assets/svg/executive-summary/ic-view-details.svg';
import { ReactComponent as TableSortIcon } from '@assets/svg/table-sort.svg';

import TableEmptyRow from '@components/common/TableEmptyRow/TableEmptyRow';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';

import { initialSorting } from '@constants/tableSorting';

import useTranslation from '@hooks/useTranslation';

import type { ITableSorting } from '@model/tableSorting';

import { selectAppLocationSelectValue } from '@store/useAppStore';

import { useActionItemsByEquipmentQuery } from '../../services/useActionItemsByEquipmentQuery';
import ActionItemsByEquipmentTableSkeleton from '../ActionItemsByEquipmentTable/ActionItemsByEquipmentTableSkeleton';
import style from './style.module.scss';

interface IProps {
  equipmentId: number;
  total: number;
  isVisibleHiddenRow: boolean;
}

const ActionItemsByEquipmentTable: React.FC<IProps> = ({ equipmentId, total, isVisibleHiddenRow }) => {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<ITableSorting>(initialSorting);

  const { customerId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const locationSelectValue = selectAppLocationSelectValue();

  const { data, isLoading } = useActionItemsByEquipmentQuery({
    equipmentId,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    sorting,
    enabled: isVisibleHiddenRow,
  });

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  return (
    <td className={style.actionItemsByEquipmentTableWrapper} colSpan={7}>
      <table className={style.actionItemsByEquipmentTable}>
        <thead>
          <tr>
            <th>
              {t('Component')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'component_name' })}
                onClick={() => onChangeSorting('component_name')}
                data-tooltip-content="component_name"
                data-tooltip-id="ai-by-equipment-sort-tooltip"
              />
            </th>
            <th>
              {t('Title')}
              <TableSortIcon
                className={cx(style.sortIcon, { [style.activeSort]: sorting.id === 'title' })}
                onClick={() => onChangeSorting('title')}
                data-tooltip-content="title"
                data-tooltip-id="ai-by-equipment-sort-tooltip"
              />
            </th>
            <th />
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <ActionItemsByEquipmentTableSkeleton total={total} />
          ) : data?.length ? (
            data?.map((row) => (
              <tr className={style.bodyRow} key={row.id}>
                <td>{row.component_name}</td>
                <td>{row.title}</td>
                <td
                  className={style.iconColumn}
                  onClick={() =>
                    navigate(`/customer/${customerId}/ai/${row.id}`, {
                      state: {
                        prevPage: pathname,
                      },
                    })
                  }
                >
                  <ViewDetailsIcon className={style.viewDetailsIcon} id="view-action-item" />
                </td>
              </tr>
            ))
          ) : (
            <TableEmptyRow text={t('No action items were found.')} />
          )}
        </tbody>
      </table>
      <Tooltip anchorSelect="#view-action-item" content={t('View Action Item')} />
      <TooltipSort id="ai-by-equipment-sort-tooltip" sorting={sorting} />
    </td>
  );
};

export default ActionItemsByEquipmentTable;
